import { create } from "zustand";

/**
 * A boolean preference the View menu toggles and localStorage remembers.
 *
 * `fallback` answers when nothing is stored — or when storage is blocked, as in
 * private mode. It is a function when the default depends on how the page was
 * reached (`isSharedArrival`), so that it is evaluated at load rather than at
 * module-definition time. A stored "1"/"0" always wins over the fallback; any
 * other stored value is treated as unset rather than as "off".
 */
export interface PersistedToggleState {
  visible: boolean;
  setVisible(v: boolean): void;
  toggle(): void;
}

export function readStoredFlag(key: string, fallback: boolean | (() => boolean)): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch {
    // Storage blocked or absent (private mode, SSR): use the fallback.
  }
  return typeof fallback === "function" ? fallback() : fallback;
}

export function writeStoredFlag(key: string, v: boolean): void {
  try {
    localStorage.setItem(key, v ? "1" : "0");
  } catch {
    // A preference that can't be persisted still applies for this session.
  }
}

export function createPersistedToggle(key: string, fallback: boolean | (() => boolean)) {
  return create<PersistedToggleState>((set, get) => ({
    visible: readStoredFlag(key, fallback),
    setVisible: (v) => {
      writeStoredFlag(key, v);
      set({ visible: v });
    },
    toggle: () => get().setVisible(!get().visible),
  }));
}
